/**
 * Shared API utility functions for unifiedtime routes
 * These functions are used across multiple API endpoints to avoid duplication
 */

// ============================================================================
// USER EMAIL UTILITIES
// ============================================================================

/**
 * Get Google user email from access token
 */
export async function getGoogleUserEmail(accessToken: string): Promise<string | null> {
  try {
    const response = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data.email || null;
  } catch (error) {
    return null;
  }
}

/**
 * Get Microsoft user email from access token
 */
export async function getMicrosoftUserEmail(accessToken: string): Promise<string | null> {
  try {
    const response = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data.mail || data.userPrincipalName || null;
  } catch (error) {
    return null;
  }
}

/**
 * Get Slack user email from access token
 */
export async function getSlackUserEmail(accessToken: string): Promise<string | null> {
  try {
    const response = await fetch("https://slack.com/api/users.profile.get", {
      headers: { 
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data.profile?.email || null;
  } catch (error) {
    return null;
  }
}

// ============================================================================
// OAUTH CREDENTIAL UTILITIES
// ============================================================================

/**
 * Get Google OAuth credentials from environment
 */
export async function getGoogleCredentials() {
  const clientId = process.env.UNIFIEDTIME_GOOGLE_OAUTH_CLIENT_ID;
  const clientSecret = process.env.UNIFIEDTIME_GOOGLE_OAUTH_CLIENT_SECRET;
  
  if (!clientId || !clientSecret) {
    throw new Error('Google OAuth credentials not configured');
  }
  
  return { clientId, clientSecret };
}

/**
 * Get Microsoft OAuth credentials from environment
 */
export async function getMicrosoftCredentials() {
  const clientId = process.env.MICROSOFT_OAUTH_CLIENT_ID;
  const clientSecret = process.env.MICROSOFT_OAUTH_CLIENT_SECRET;
  
  if (!clientId || !clientSecret) {
    throw new Error('Microsoft OAuth credentials not configured');
  }
  
  return { clientId, clientSecret };
}

// ============================================================================
// TOKEN REFRESH UTILITIES — DEPRECATED, DO NOT ADD TO
// ============================================================================
//
// USER_OAUTH_TOKEN_REFRESH: the monorepo has exactly ONE OAuth refresh
// implementation — `createTokenService` in `@wtc/oauth-connections`. The helper
// below predates it and is the LAST survivor of this file's refresh cluster.
//
// DELETED here (2026-08-17, Phase 4.5 wave 4), all with zero consumers anywhere
// in `apps/*` or `packages/*`: `refreshMicrosoftAccessToken` (a whole duplicate
// `grant_type=refresh_token` POST), the `refreshAccessToken` provider-switching
// wrapper, and `getGoogleUserEmailWithRefresh` / `getMicrosoftUserEmailWithRefresh`
// / `getUserEmail` — the lazy "probe userinfo, refresh if it 401s" wrappers whose
// only reason to exist was that refresh.
//
// DELETED (2026-08-17, Phase 4.5 wave 5): `refreshGoogleAccessToken`. Wave 4 kept
// it for exactly one live call site,
// `apps/unifiedtime/src/app/api/unifiedtime/getCalendar/google/[id]/route.ts`, which
// could not migrate because `apps/unifiedtime` had no `ConnectionStore` adapter for
// `msft_tokens` / `google_tokens` — the only one was hub-private. Wave 5 extracted
// that adapter into `@wtc/unifiedtime-tokens`, the route adopted
// `unifiedTimeRawFetch` wholesale, and this helper's last consumer went away with it.
//
// THIS FILE NOW CONTAINS NO REFRESH AT ALL. Do not add one back: `@wtc/ui`'s index is
// a browser-reachable barrel shipped to sold products, so it must never hold token
// refresh — and it must never import `@wtc/oauth-connections` / `@wtc/unifiedtime-tokens`
// either (both pinned by `src/lib/__tests__/refresh-consolidation.test.ts`). A module
// here that needs a token takes one from its caller, which is server code.
//
// The credential getters above STAY: `getGoogleCredentials` / `getMicrosoftCredentials`
// are read by connect-flow code-exchange paths, which are not refresh.

// ============================================================================
// CALENDAR EVENT FETCHING UTILITIES
// ============================================================================

/**
 * Fetch Google Calendar events
 */
export async function fetchGoogleCalendarEvents(
  token: string, 
  userEmail: string, 
  timeMin: string, 
  timeMax: string
) {
  const calendarItems = [];
  let nextLink: string | null = `https://www.googleapis.com/calendar/v3/calendars/${userEmail}/events?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=2500`;

  while (nextLink) {
    try {
      const response: Response = await fetch(nextLink, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Google Calendar API error: ${response.status}`);
      }

      const data: any = await response.json();
      calendarItems.push(...data.items);
      nextLink = data.nextPageToken ? `${nextLink}&pageToken=${data.nextPageToken}` : null;
    } catch (error) {
      console.error('Error fetching Google calendar events:', error);
      break;
    }
  }

  return calendarItems;
}

/**
 * Fetch Microsoft Calendar events
 */
export async function fetchMicrosoftCalendarEvents(
  token: string, 
  userEmail: string, 
  timeMin: string, 
  timeMax: string
) {
  const calendarItems = [];
  let nextLink: string | null = `https://graph.microsoft.com/v1.0/me/calendarView?startDateTime=${timeMin}&endDateTime=${timeMax}&$orderby=start/dateTime asc&$top=50`;

  while (nextLink) {
    try {
      const response: Response = await fetch(nextLink, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Microsoft Calendar API error: ${response.status}`);
      }

      const data: any = await response.json();
      calendarItems.push(...data.value);
      nextLink = data['@odata.nextLink'] || null;
    } catch (error) {
      console.error('Error fetching Microsoft calendar events:', error);
      break;
    }
  }

  return calendarItems;
}

/**
 * Fetch external Google Calendar events (for public booking)
 */
export async function fetchExternalGoogleCalendarEvents(
  token: string, 
  userEmail: string, 
  timeMin: string, 
  timeMax: string
) {
  const calendarItems = [];
  let nextLink: string | null = `https://www.googleapis.com/calendar/v3/calendars/${userEmail}/events?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=2500`;

  while (nextLink) {
    try {
      const response: Response = await fetch(nextLink, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Google Calendar API error: ${response.status}`);
      }

      const data: any = await response.json();
      calendarItems.push(...data.items);
      nextLink = data.nextPageToken ? `${nextLink}&pageToken=${data.nextPageToken}` : null;
    } catch (error) {
      console.error('Error fetching external Google calendar events:', error);
      break;
    }
  }

  return calendarItems;
}

/**
 * Fetch external Microsoft Calendar events (for public booking)
 */
export async function fetchExternalMicrosoftCalendarEvents(
  token: string, 
  userEmail: string, 
  timeMin: string, 
  timeMax: string
) {
  const calendarItems = [];
  let nextLink: string | null = `https://graph.microsoft.com/v1.0/me/calendarView?startDateTime=${timeMin}&endDateTime=${timeMax}&$orderby=start/dateTime asc&$top=50`;

  while (nextLink) {
    try {
      const response: Response = await fetch(nextLink, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Microsoft Calendar API error: ${response.status}`);
      }

      const data: any = await response.json();
      calendarItems.push(...data.value);
      nextLink = data['@odata.nextLink'] || null;
    } catch (error) {
      console.error('Error fetching external Microsoft calendar events:', error);
      break;
    }
  }

  return calendarItems;
}

/**
 * Fetch calendar events with pagination (generic)
 */
export async function fetchCalendarEvents(
  token: string, 
  userEmail: string, 
  timeMin: string, 
  timeMax: string,
  provider: 'google' | 'microsoft' = 'google'
) {
  if (provider === 'google') {
    return fetchGoogleCalendarEvents(token, userEmail, timeMin, timeMax);
  } else {
    return fetchMicrosoftCalendarEvents(token, userEmail, timeMin, timeMax);
  }
}

/**
 * Fetch external calendar events with pagination (generic)
 */
export async function fetchExternalCalendarEvents(
  token: string, 
  userEmail: string, 
  timeMin: string, 
  timeMax: string,
  provider: 'google' | 'microsoft' = 'google'
) {
  if (provider === 'google') {
    return fetchExternalGoogleCalendarEvents(token, userEmail, timeMin, timeMax);
  } else {
    return fetchExternalMicrosoftCalendarEvents(token, userEmail, timeMin, timeMax);
  }
}

// ============================================================================
// INPUT VALIDATION UTILITIES
// ============================================================================

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Sanitize user input to prevent XSS
 */
export function sanitizeInput(input: string): string {
  return input.replace(/[<>&'"]/g, '');
}

// ============================================================================
// COMMON RESPONSE UTILITIES
// ============================================================================

/**
 * Create a standard error response
 */
export function createErrorResponse(message: string, status: number = 500) {
  return new Response(JSON.stringify({ error: message }), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    },
  });
}

/**
 * Create a standard success response
 */
export function createSuccessResponse(data: any, status: number = 200) {
  return new Response(JSON.stringify(data), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    },
  });
}

/**
 * Handle CORS preflight requests
 */
export function handleCorsPreflight() {
  return new Response(null, {
    status: 200,
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization',
    },
  });
}

/**
 * Create CORS headers for responses
 */
export function createCorsHeaders() {
  return {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
  };
}

/**
 * Handle HTTP method not allowed
 */
export function createMethodNotAllowedResponse() {
  return new Response(JSON.stringify({ error: 'Method not allowed' }), {
    status: 405,
    headers: {
      'Content-Type': 'application/json',
      ...createCorsHeaders()
    },
  });
}

/**
 * Handle authentication required
 */
export function createAuthRequiredResponse() {
  return new Response(JSON.stringify({ error: 'Authentication required' }), {
    status: 401,
    headers: {
      'Content-Type': 'application/json',
      ...createCorsHeaders()
    },
  });
}

/**
 * Handle missing parameters
 */
export function createMissingParamsResponse(message: string) {
  return new Response(JSON.stringify({ error: message }), {
    status: 400,
    headers: {
      'Content-Type': 'application/json',
      ...createCorsHeaders()
    },
  });
}

// ============================================================================
// ACCOUNT STATUS INTERFACE
// ============================================================================

export interface AccountStatus {
  google: {
    primary: boolean;
    secondary: boolean;
    primaryEmail?: string;
    secondaryEmail?: string;
  };
  microsoft: boolean;
  microsoftEmail?: string;
  /** Hub can load WTC Email (mail app) calendar via server bridge without Google/Microsoft OAuth */
  wtcEmailCalendarBridgeConfigured?: boolean;
}
